import { Helmet, HelmetProvider } from "react-helmet-async";
import { useEffect, useState } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from "@heroui/react";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { Education, Eye, Plus } from "assets/icons";
import ButtonNew from "components/buttonNew/buttonNew";
import AddCompanyModal from "./components/AddCompanyModal";
import { Archive, ArrowsMerge, CheckCircle, FileText, Info } from "@phosphor-icons/react";
import MergeCompanyModal from "./components/MergeCompanyModal";
import { useHasPermission } from "hooks/usePermissions";

const Vennootschappen = () => {
  const [companies, setCompanies] = useState<any[]>([]);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);

  const navigate = useNavigate();
  const { session } = useSession();
  const canAccessCorporate = useHasPermission("canAccessCorporate");

  // Determine if user has edit rights
  // For now: Anyone with Corporate access can edit (backward compatible)
  // Future: Check canEditCorporate for granular control
  const hasEditRights = canAccessCorporate;

  const handleCompanyModalOpen = () => setIsCompanyModalOpen(true);
  const handleCompanyModalClose = () => setIsCompanyModalOpen(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      // This overview is the one place that shows the archief (with an archive/merge
      // badge), so it explicitly asks for archived companies too.
      const response = await fetchWithAuth(
        `${API_URL}/companies/all?includeArchived=true`,
        { method: "GET" },
        session,
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const companies = await response.json();
      setCompanies(companies);
    } catch (error) {
      console.error("Failed to load Yally companies:", error);
      toast.error("Failed to load Yally companies.");
    }
  };

  const getCompanyStatus = (company: any) => {
    if (company.archived && company.mergedWithCompanyId) {
      return (
        <Tooltip
          content={`Gefuseerd met bedrijf met ID ${company.mergedWithCompanyId}`}
        >
          <ArrowsMerge className="text-brick-primary h-[20px] w-[20px]" />
        </Tooltip>
      );
    } else if (company.archived) {
      return <Archive className="text-brick-primary h-[20px] w-[20px]" />;
    } else {
      return <CheckCircle className="text-moss-primary h-[20px] w-[20px]" />;
    }
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Vennootschappen &mdash; Yally</title>
        </Helmet>
      </HelmetProvider>
      <div className="z-10 mb-10 flex flex-col rounded-lg bg-white p-4 shadow-md">
        <div className="flex flex-col gap-2.5">
          <header className="flex flex-row items-center justify-between">
            <div className="flex flex-row items-center gap-1.5">
              <Education className="h-[18px] w-[18px]" />
              <p className="text-lg">Yally vennootschappen</p>
            </div>
            {hasEditRights && (
              <div className="flex gap-2">
                <ButtonNew
                  variant="primaryBrick"
                  onClick={() => setIsMergeModalOpen(true)}
                  icon={<ArrowsMerge />}
                  label="Bedrijven fuseren"
                />
                <ButtonNew
                  variant="primaryMoss"
                  onClick={handleCompanyModalOpen}
                  icon={<Plus />}
                  label="Vennootschap toevoegen"
                />
              </div>
            )}
          </header>
          <Table
            classNames={{
              wrapper: "shadow-none rounded-none p-0",
              thead: "h-fit border-b-2 border-neutral-primary/40",
              th: "rounded-none! bg-white py-2 h-fit text-sm text-neutral-primary font-medium",
              tbody: "border-b divide-y divide-neutral-primary/15",
              tr: "divide-x divide-neutral-primary/15",
              td: "py-2.5",
            }}
          >
            <TableHeader>
              <TableColumn>Status</TableColumn>
              <TableColumn>Vennootschapsnaam</TableColumn>
              <TableColumn>Juridische vorm</TableColumn>
              <TableColumn>KBO nummer</TableColumn>
              <TableColumn>BTW nummer</TableColumn>
              <TableColumn>Beroepskoper nr.</TableColumn>
              <TableColumn>Detail</TableColumn>
            </TableHeader>
            <TableBody>
              {companies.map((company) => (
                <TableRow key={company.id}>
                  <TableCell>{getCompanyStatus(company)}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {company.name}{" "}
                      <Tooltip content={`Bedrijf ID: ${company.id}`}>
                        <Info />
                      </Tooltip>
                    </div>
                  </TableCell>
                  <TableCell>{company.legalForm}</TableCell>
                  <TableCell>{company.kbo}</TableCell>
                  <TableCell>{company.vat}</TableCell>
                  <TableCell>{company.numberProfessionalSeller}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Tooltip content="Vennootschapsdetails">
                        <button
                          type="button"
                          aria-label="Vennootschapsdetails openen"
                          onClick={() => navigate(`/vennootschappen/${company.id}`)}
                          className={`flex h-5 w-5 items-center justify-center ${hasEditRights ? "text-moss-primary hover:text-moss-primary/80" : "text-blue-600 hover:text-blue-700"}`}
                        >
                          <Eye className="h-[20px] w-[20px]" />
                        </button>
                      </Tooltip>
                      <Tooltip content="Documenten openen">
                        <button
                          type="button"
                          aria-label="Documenten openen"
                          onClick={() =>
                            navigate(
                              `/vennootschappen/${company.id}?tab=documents`,
                            )
                          }
                          className="text-moss-primary hover:text-moss-primary/80"
                        >
                          <FileText
                            className="h-[20px] w-[20px]"
                            weight="duotone"
                          />
                        </button>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      <AddCompanyModal
        isOpen={isCompanyModalOpen}
        onClose={handleCompanyModalClose}
        refreshParentCompanies={fetchCompanies}
      />
      <MergeCompanyModal
        isOpen={isMergeModalOpen}
        onClose={() => setIsMergeModalOpen(false)}
        companies={companies}
        refreshParentCompanies={fetchCompanies}
      />
    </>
  );
};

export default Vennootschappen;
